import { Inject, inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { filter, map, Observable, of, switchMap, take, timeout } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AUTH_STATE } from '../firebase.providers';
import { User } from 'firebase/auth';

// Two-step check: first the resolved Firebase auth state (same page-refresh rule as
// AuthGuard), then the admin claim on the token.
//
// The claim, not the Firestore user document: firestore.rules grants admin on
// `request.auth.token.admin`, so the token is what the backend actually enforces and the
// document is a copy of it. Waiting for that copy put a network round trip in front of
// every admin screen, and when the backend was slow to answer during a cold start the
// 5s timeout bounced the admin to /start -- a member opening /beheer from a bookmark on
// a phone landed on the home page and had to try again. It is also the same fact the
// menu is built from, so what the menu offers and what the guard allows cannot disagree.
//
// Fails closed at both steps: /login with the page asked for, or /start.
@Injectable({
  providedIn: 'root',
})
export class AdminCheckService {
  constructor(
    @Inject(AUTH_STATE) private authState: Observable<User | null>,
    private authService: AuthService,
    private router: Router,
  ) {}

  canActivate(requestedUrl: string): Observable<boolean | UrlTree> {
    return this.authState.pipe(
      take(1),
      timeout({ first: 5000, with: () => of(null) }),
      switchMap((firebaseUser) => {
        // Nobody signed in: send them to sign in and come back here. A signed-in
        // non-admin still goes to /start below, which says nothing about whether
        // the page exists.
        if (!firebaseUser || !firebaseUser.emailVerified) {
          return of(
            this.router.createUrlTree(['/login'], {
              queryParams: { returnUrl: requestedUrl },
            }),
          );
        }
        return this.authService.navReady$.pipe(
          filter(Boolean),
          take(1),
          timeout({ first: 5000, with: () => of(true) }),
          switchMap(() => this.authService.userIsAdmin$.pipe(take(1))),
          map((isAdmin) => (isAdmin ? true : this.router.parseUrl('/start'))),
        );
      }),
    );
  }
}

export const AdminGuard: CanActivateFn = (_route, state) =>
  inject(AdminCheckService).canActivate(state.url);
